//#region src/media/types/index.ts
/**
* Floating-point tolerance for matching segments by `startTime`. Two segments are considered the same position when
* `Math.abs(a.startTime - b.startTime) < SEGMENT_TIME_EPSILON`. Used by the source-buffer dedup and segment-loader
* quality-aware filter to tolerate sub-millisecond drift in segment timestamps across multiple playlists / quality
* levels.
*/
const SEGMENT_TIME_EPSILON = 1e-4;
/** Key under `Ham.metadata` where {@link MediaPlaylistMetadata} is stored. */
const MEDIA_PLAYLIST_METADATA_KEY = "mediaPlaylist";
/** Typed read of the media-playlist metadata stashed in `ham.metadata`. */
function getMediaPlaylistMetadata(ham) {
	return ham.metadata?.[MEDIA_PLAYLIST_METADATA_KEY];
}
/** Key under `Ham.metadata` where {@link MultivariantPlaylistMetadata} is stored. */
const MULTIVARIANT_PLAYLIST_METADATA_KEY = "multivariantPlaylist";
/** Typed read of the multivariant-playlist metadata stashed in `ham.metadata`. */
function getMultivariantPlaylistMetadata(ham) {
	return ham.metadata?.[MULTIVARIANT_PLAYLIST_METADATA_KEY];
}
/**
* The presentation's `#EXT-X-SESSION-DATA` entries, optionally narrowed to one `DATA-ID`. Empty when the playlist
* carried none, so consumers need no presence check.
*/
function getSessionData(ham, dataId) {
	const entries = getMultivariantPlaylistMetadata(ham)?.sessionData ?? [];
	return dataId === void 0 ? entries : entries.filter((entry) => entry.dataId === dataId);
}
/**
* Derive {@link StreamType} from a media playlist's metadata. Per the model, only `#EXT-X-PLAYLIST-TYPE:VOD` marks
* on-demand; everything else (EVENT, or the tag absent) is live — completeness (`endList`) never factors in.
*/
function deriveStreamType(metadata) {
	return metadata?.playlistType === "VOD" ? "on-demand" : "live";
}
function isResolvedTrack(track) {
	return "segments" in track;
}
/** Check if a presentation has duration (at least one track resolved). Narrows type to include required duration. */
function hasPresentationDuration(presentation) {
	return presentation.duration !== void 0;
}
/**
* Narrows a `MaybeResolvedPresentation` to a fully resolved `Presentation`.
*
* A presentation is resolved once `resolvePresentation` has parsed the manifest and populated both `id` and
* `selectionSets`. Both must be present — a partial value with only one of them isn't usable, and letting it through
* would have downstream behaviors crash when they access `selectionSets`.
*/
function isResolvedPresentation(presentation) {
	return presentation !== void 0 && presentation.id !== void 0 && presentation.selectionSets !== void 0;
}
//#endregion
export { MEDIA_PLAYLIST_METADATA_KEY, MULTIVARIANT_PLAYLIST_METADATA_KEY, SEGMENT_TIME_EPSILON, deriveStreamType, getMediaPlaylistMetadata, getMultivariantPlaylistMetadata, getSessionData, hasPresentationDuration, isResolvedPresentation, isResolvedTrack };

//# sourceMappingURL=index.js.map